/**
 * Cart revalidation API contract — request and response zod schemas.
 *
 * POST /v1/cart/revalidate
 *
 * The endpoint is read-only: it accepts a set of cart line specs and returns
 * ground-truth pricing + availability facts. The diff (baked vs current) is
 * computed on the client in cart-revalidate.ts.
 *
 * Source: 2026-06-20-wandero-cart-price-revalidation spec.
 */

import { z } from 'zod';
import { CurrencyCode } from '../site.js';

/** One line to revalidate. Only the fields needed for price + availability. */
export const CartRevalidateItemSchema = z.object({
  // Stable client id, echoed back so the client can match results to lines.
  item_id: z.string().uuid(),
  wallpaper_id: z.number().int().positive(),
  material_id: z.number().int().positive(),
  // Covered area in m² (already folds roll_count — see formula footgun).
  area_m2: z.number().positive(),
});
export type CartRevalidateItem = z.infer<typeof CartRevalidateItemSchema>;

export const CartRevalidateRequestSchema = z.object({
  currency: CurrencyCode,
  // Country for wallpaper visibility override (defaults PL; A-Manager site).
  country_code: z.string().length(2).default('PL'),
  // Bound the batch to keep the endpoint cheap + non-abusable.
  items: z.array(CartRevalidateItemSchema).min(1).max(100),
});
export type CartRevalidateRequest = z.infer<typeof CartRevalidateRequestSchema>;

/** Per-line facts. The client does the diff; this returns ground truth only. */
export const CartRevalidateResultItemSchema = z.object({
  item_id: z.string().uuid(),
  // null when the material has no current price row (treat as unavailable).
  current_unit_price: z.string().nullable(),
  // calcSumRow(area_m2, current_unit_price); null when unit price is null.
  current_price_total: z.string().nullable(),
  // Discount-engine effective price (effectivePrice via schedules); null when no discount applies.
  current_price_discounted: z.string().nullable(),
  // false when material not public OR wallpaper not 'published'.
  available: z.boolean(),
});
export type CartRevalidateResultItem = z.infer<typeof CartRevalidateResultItemSchema>;

export const CartRevalidateResponseSchema = z.object({
  currency: CurrencyCode,
  items: z.array(CartRevalidateResultItemSchema),
});
export type CartRevalidateResponse = z.infer<typeof CartRevalidateResponseSchema>;
